'use client';

import { useSearchParams } from 'react-router-dom';
import { useExecutionStore } from '@/lib/storage/provider';

/**
 * Debug panel — rendered only when ?debug=1 is present in the URL.
 *
 * Shows live values from the execution worker:
 *   workerId   — identity of the active SharedWorker instance
 *   workerState — internal state machine (UNINITIALIZED / READY / etc.)
 *   inFlight    — outstanding fetch requests
 *   coveragePct — % of the time range covered by the local IDB cache
 *   lastSyncAt  — epoch ms of the last successful sync
 *
 * No UI in production — the `?debug=1` check is a compile-time dead branch
 * that the bundler will tree-shake in builds where the param is absent.
 */
export function DebugPanel(): import('react/jsx-runtime').JSX.Element | null {
  const [searchParams] = useSearchParams();
  const isDebug = searchParams.get('debug') === '1';

  const {
    workerId,
    workerState,
    inFlight,
    coveragePct,
    gapCount,
    lastSyncAt,
    lastErrorAt,
    isSyncing,
    error,
  } = useExecutionStore();

  if (!isDebug) return null;

  const rows: Array<{ label: string; value: React.ReactNode }> = [
    { label: 'workerId', value: workerId ?? '—' },
    { label: 'workerState', value: workerState ?? '—' },
    {
      label: 'inFlight',
      value: <span className={inFlight > 0 ? 'text-amber-400' : undefined}>{inFlight}</span>,
    },
    {
      label: 'coveragePct',
      value: (
        <span>
          {coveragePct}%
          {gapCount > 0 && (
            <span className="ml-1 text-amber-400">
              ({gapCount} gap{gapCount !== 1 ? 's' : ''})
            </span>
          )}
        </span>
      ),
    },
    {
      label: 'lastSyncAt',
      value: lastSyncAt != null ? new Date(lastSyncAt).toISOString() : '—',
    },
    {
      label: 'lastErrorAt',
      value:
        lastErrorAt != null ? (
          <span className="text-red-400">{new Date(lastErrorAt).toISOString()}</span>
        ) : (
          '—'
        ),
    },
    {
      label: 'isSyncing',
      value: (
        <span className={isSyncing ? 'text-[var(--flux-accent)]' : 'text-muted-foreground'}>
          {String(isSyncing)}
        </span>
      ),
    },
    {
      label: 'error',
      value: error != null ? <span className="text-red-400">{error}</span> : '—',
    },
  ];

  return (
    <div className="fixed bottom-4 right-4 z-[9999] rounded-lg border border-white/10 bg-black/80 p-3 font-mono text-xs text-white/80 shadow-xl backdrop-blur-sm min-w-[280px]">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-semibold text-white">⚡ Execution Debug</span>
        <span className="text-white/40 text-[10px]">?debug=1</span>
      </div>
      <table className="w-full border-collapse">
        <tbody>
          {rows.map(({ label, value }) => (
            <tr key={label} className="border-t border-white/5">
              <td className="py-0.5 pr-3 text-white/50">{label}</td>
              <td className="py-0.5 text-right break-all">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
